import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Prop,
  State,
} from "@stencil/core";
import { dealerSearchResultsStore } from "../../state/dealer-search-results-store";
import { navStateStore } from "@phn/state/nav-state-store";
import { getAllPPNDealersForMarket } from "../../services/dealer/dealer-service";
import logger from "@phn/services/logger";
import { getErrorMessage } from "@phn/utility/navi-error";
import { componentsOnReady } from "@phn/utility/ready";
import { Timeout } from "@phn/utility/timeout";
import { navContentStore } from "@phn/state/nav-content-store";
import analyticsManager from "../../services/analytics/analytics-manager";
import {
  DEALER_MARKET_SEARCH_TERM,
  DEALER_SEARCH_RESULT_DISPLAY_ELEMENT_ID_DOUBLE_DRAWER,
  ElementTypes,
  EventActions,
} from "@phn/utility/constants";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import { getConfig, isString } from "@phn/utility/helper";
import { splitLocale } from "@phn/entities/locale";
import ErrorMessage from "../dealer-search-form/error-message";

@Component({
  tag: "phn-nd-dealer-search-by-number-of-dealers",
  styleUrl: "dealer-search-by-number-of-dealers.scss",
  scoped: true,
})
export class DealerSearchByNumberOfDealers {
  @State() isLoading = false;

  @State() formErrorMessage: string | null = null;

  @Prop() locale!: string;

  @Element() el!: HTMLPhnNdDealerSearchByNumberOfDealersElement;

  @Event() removeSkeletonOnDealerSearchForm!: EventEmitter;

  @Event() loadingResults!: EventEmitter<boolean>;

  componentWillLoad(): void {
    this.waitForComponentsToLoad().then(() => {
      const { dealer } = navContentStore.state;
      if (dealer.numberOfDealers <= 5) {
        this.fetchAllDealersForMarket();
      }
    });
  }

  componentDidLoad(): void {
    this.emitPageLoadEvent();
  }

  private async waitForComponentsToLoad() {
    await componentsOnReady(this.el, new Timeout(5000));
    this.removeSkeletonOnDealerSearchForm.emit();
  }

  private async fetchAllDealersForMarket() {
    const { error: errorContent } = navContentStore.state;

    try {
      this.isLoading = true;
      this.loadingResults.emit(true);

      const { country } = splitLocale(this.locale);
      const { env } = navStateStore.state;

      const ppnDealerSearchResults = await getAllPPNDealersForMarket(
        env,
        getConfig(env).BFF_ENDPOINT,
        country,
      );

      dealerSearchResultsStore.state.results = ppnDealerSearchResults.map(
        ({ ppnDealer: dealer }) => dealer.id,
      );
      dealerSearchResultsStore.state.ppnDealers = ppnDealerSearchResults.reduce(
        (dealers, dealerSearchResult) => {
          return {
            ...dealers,
            [dealerSearchResult.ppnDealer.id]: dealerSearchResult,
          };
        },
        dealerSearchResultsStore.state.ppnDealers,
      );
    } catch (err) {
      logger.error("FETCH_DEALERS", err as Error);
      this.formErrorMessage = getErrorMessage(errorContent, err as Error);
    } finally {
      this.loadingResults.emit(false);
      this.isLoading = false;
      this.emitResultsLoadEvent();
    }
  }

  private emitPageLoadEvent() {
    const { dealer } = navContentStore.state;

    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.DEALER_SEARCH_SCREEN_LOAD,
      {
        componentDisplay: {
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementType: analyticsManager.calculateDisplayElementType(
            "",
            ElementTypes.dealerSearchForm,
          ),
          displayElementName: dealer.text,
        },
        componentSearch: {
          searchTerm: DEALER_MARKET_SEARCH_TERM,
          numberOfResults: dealer.numberOfDealers,
        },
      },
    );
  }

  private emitResultsLoadEvent() {
    const { dealer } = navContentStore.state;

    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.DEALER_RESULTS_SCREEN_LOAD,
      {
        componentDisplay: {
          displayElementId:
            analyticsManager.getDealerResultsLoadDisplayElementId(
              DEALER_SEARCH_RESULT_DISPLAY_ELEMENT_ID_DOUBLE_DRAWER,
            ),
          displayElementType:
            analyticsManager.getDealerResultsLoadDisplayElementType(),
          displayElementName: dealer.text,
        },
        componentSearch: {
          searchTerm: DEALER_MARKET_SEARCH_TERM,
          numberOfResults: dealer.numberOfDealers,
        },
      },
    );
  }

  private reset() {
    this.formErrorMessage = null;
    this.isLoading = false;
    this.loadingResults.emit(false);
    dealerSearchResultsStore.state.results = null;
  }

  render() {
    if (isString(this.formErrorMessage))
      return (
        <ErrorMessage
          errorMessage={this.formErrorMessage}
          onDismiss={() => this.reset()}
        />
      );

    return null;
  }
}
